import React, { useEffect, useRef, useState } from 'react';
import { postCandidateSupportChat } from '../../api/candidateSupport';

/**
 * Candidate help chatbot: a floating launcher at the bottom-left of every candidate
 * portal page. Closed, it shows a short "I'm here to help" teaser; open, it is a chat
 * window titled "Samastix Help Assistant" backed by /api/candidate-support/chat
 * (rescheduling, disconnects, join time, agent questions).
 */

const GREETING =
  "Hi! I'm the Samastix Help Assistant. I can help with interview timing, disconnects, "
  + 'rescheduling (up to 2 schedule changes) and the desktop agent. What do you need?';

const QUICK_REPLIES = [
  'Reschedule my interview',
  'I got disconnected',
  'When can I join my interview?',
  'Help with the desktop agent',
];

const FALLBACK = 'Support is temporarily unavailable. Please email careers@samastix.com.';
const TEASER_KEY = 'smx.helpTeaserDismissed';

function readTeaserDismissed() {
  try { return window.sessionStorage.getItem(TEASER_KEY) === '1'; } catch { return false; }
}
function writeTeaserDismissed() {
  try { window.sessionStorage.setItem(TEASER_KEY, '1'); } catch { /* storage unavailable */ }
}

function ChatIcon({ className = 'w-6 h-6' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />
      <path d="M8.5 11h.01M12 11h.01M15.5 11h.01" />
    </svg>
  );
}

function CloseIcon({ className = 'w-4 h-4' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
      className={className} aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      strokeLinejoin="round" className="w-4 h-4" aria-hidden="true">
      <path d="M22 2L11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

export default function SupportChatWidget() {
  const [open, setOpen] = useState(false);
  const [teaser, setTeaser] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: GREETING }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // Show the teaser a moment after the page loads, unless dismissed this session.
  useEffect(() => {
    if (readTeaserDismissed()) return undefined;
    const t = setTimeout(() => setTeaser(true), 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending, open]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const openChat = () => {
    setOpen(true);
    setTeaser(false);
    writeTeaserDismissed();
  };

  const dismissTeaser = (e) => {
    e.stopPropagation();
    setTeaser(false);
    writeTeaserDismissed();
  };

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || sending) return;
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setSending(true);
    try {
      const res = await postCandidateSupportChat({
        message: text,
        history: next.slice(-8).map((m) => ({ role: m.role, content: m.content })),
      });
      const reply = res.data?.reply || 'Sorry, I could not help with that right now.';
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
      if (res.data?.rescheduled) {
        window.dispatchEvent(new CustomEvent('samastix:schedules-changed'));
      }
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', content: FALLBACK }]);
    } finally {
      setSending(false);
    }
  };

  const showQuickReplies = messages.length === 1 && !sending;

  return (
    <div className="fixed bottom-5 right-5 z-[60] flex flex-col items-end gap-3 pointer-events-none">
      {open && (
        <section
          role="dialog"
          aria-label="Samastix Help Assistant"
          className="pointer-events-auto w-[min(360px,calc(100vw-40px))] h-[min(540px,calc(100vh-120px))] flex flex-col
                     bg-white rounded-[18px] border border-[var(--line)] shadow-[0_24px_60px_rgba(8,20,43,0.28)] overflow-hidden"
        >
          <header className="flex items-center gap-3 px-4 py-3 text-white"
            style={{ background: 'linear-gradient(135deg, var(--navy-950), var(--navy-800))' }}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center bg-[var(--teal-500)] shrink-0">
              <ChatIcon className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                Samastix Help Assistant
              </h2>
              <p className="text-[11px] text-white/70 flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--teal-400)] inline-block" />
                Here to help with interviews and rescheduling
              </p>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close help chat"
              className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10">
              <CloseIcon />
            </button>
          </header>

          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[var(--canvas)]" aria-live="polite">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2 text-[13px] leading-relaxed whitespace-pre-wrap break-words ${
                    m.role === 'user'
                      ? 'bg-[var(--teal-600)] text-white rounded-[14px] rounded-br-[4px]'
                      : 'bg-white text-[var(--ink)] border border-[var(--line)] rounded-[14px] rounded-br-[4px] shadow-sm'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" aria-label="Assistant is typing">
                <div className="bg-white border border-[var(--line)] rounded-[14px] rounded-br-[4px] px-3.5 py-2.5 flex gap-1">
                  {[0, 150, 300].map((d) => (
                    <span key={d} className="w-1.5 h-1.5 rounded-full bg-[var(--ink-soft)] animate-bounce"
                      style={{ animationDelay: `${d}ms` }} />
                  ))}
                </div>
              </div>
            )}
            {showQuickReplies && (
              <div className="flex flex-wrap gap-2 pt-1">
                {QUICK_REPLIES.map((q) => (
                  <button key={q} type="button" onClick={() => send(q)}
                    className="text-xs px-3 py-1.5 rounded-full border border-[var(--teal-500)] text-[var(--teal-700)]
                               bg-white hover:bg-[var(--teal-050)]">
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form className="flex items-center gap-2 px-3 py-3 border-t border-[var(--line)] bg-white"
            onSubmit={(e) => { e.preventDefault(); send(); }}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question…"
              aria-label="Message the help assistant"
              className="input flex-1 !py-2 !text-sm"
              disabled={sending}
              maxLength={1000}
            />
            <button type="submit" disabled={sending || !input.trim()} aria-label="Send message"
              className="btn btn-primary !p-0 w-9 h-9 !rounded-full flex items-center justify-center shrink-0">
              <SendIcon />
            </button>
          </form>
          <p className="text-[10px] text-center text-[var(--ink-soft)] pb-2 bg-white">
            Automated assistant. For anything else, email careers@samastix.com
          </p>
        </section>
      )}

      {!open && teaser && (
        <div
          role="status"
          onClick={openChat}
          className="pointer-events-auto cursor-pointer relative max-w-[250px] bg-white border border-[var(--line)]
                     rounded-[14px] rounded-br-[4px] shadow-[0_12px_32px_rgba(8,20,43,0.18)] pl-3.5 pr-8 py-2.5"
        >
          <p className="text-[13px] font-semibold text-[var(--navy-950)]">Need any help?</p>
          <p className="text-xs text-[var(--ink-soft)] mt-0.5">
            I&apos;m here to help with your interviews, rescheduling or the desktop agent.
          </p>
          <button type="button" onClick={dismissTeaser} aria-label="Dismiss"
            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center
                       text-[var(--ink-soft)] hover:bg-[var(--canvas)]">
            <CloseIcon className="w-3 h-3" />
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openChat())}
        aria-label={open ? 'Close help chat' : 'Open help chat'}
        aria-expanded={open}
        className="pointer-events-auto relative w-14 h-14 rounded-full flex items-center justify-center text-white
                   shadow-[0_12px_28px_rgba(13,148,136,0.45)] hover:scale-105 active:scale-95 transition-transform"
        style={{ background: 'linear-gradient(135deg, var(--teal-500), var(--teal-700))' }}
      >
        {open ? <CloseIcon className="w-5 h-5" /> : <ChatIcon />}
        {!open && (
          <span className="absolute top-0.5 right-0.5 w-3 h-3 rounded-full bg-[var(--teal-400)] border-2 border-white" />
        )}
      </button>
    </div>
  );
}
